import { StateCreator } from 'zustand';

import { toDisplayFields } from './displayFields';
import type {
  ComposeSnapshotIndexEntry,
  ComposeSnapshotIndexState,
  ComposeSnapshotIndexStore,
} from './types';
import { ComposeSnapshotChangeType } from '../../enums/composeSnapshotChangeType';
import { ComposeSnapshotStatus } from '../../enums/composeSnapshotStatus';
import type { ComposeSnapshot } from '../../models/composeSnapshot';

export const INITIAL_COMPOSE_SNAPSHOT_INDEX_STATE: ComposeSnapshotIndexState = {
  userId: undefined,
  entriesBySessionId: {},
  liveSessionIds: {},
  openComposeSessionIds: {},
  claimedSourceSessionIds: {},
  backgroundSavingSessionIds: {},
  lastLiveAtBySessionId: {},
  syncingSessionIds: {},
  syncedDraftIdBySessionId: {},
  verifiedDraftIds: {},
};

export const toIndexEntry = (
  snapshot: ComposeSnapshot,
): ComposeSnapshotIndexEntry => ({
  sessionId: snapshot.sessionId,
  lineageId: snapshot.lineageId ?? snapshot.sessionId,
  draftId: snapshot.draftId,
  sourceMessageId: snapshot.sourceMessageId,
  handler: snapshot.handler,
  inboxEntryId: snapshot.inboxEntryId,
  fromEmail: snapshot.fromEmail,
  updatedAt: snapshot.updatedAt,
  syncBlockedReason: snapshot.syncBlockedReason,
  ...toDisplayFields(snapshot),
});

const withoutKey = <T>(
  record: Record<string, T>,
  key: string,
): Record<string, T> => {
  const { [key]: _removed, ...rest } = record;
  return rest;
};

export const createComposeSnapshotIndexSlice: StateCreator<
  ComposeSnapshotIndexStore
> = (set) => ({
  ...INITIAL_COMPOSE_SNAPSHOT_INDEX_STATE,

  hydrate: (userId, snapshots) =>
    set({
      userId,
      entriesBySessionId: Object.fromEntries(
        snapshots
          .filter((s) => s.status === ComposeSnapshotStatus.DIRTY)
          .map((s) => [s.sessionId, toIndexEntry(s)]),
      ),
    }),

  applyChange: (change) =>
    set((state) => {
      if (change.type === ComposeSnapshotChangeType.PUT) {
        const { snapshot } = change;
        if (
          snapshot.userId !== state.userId ||
          snapshot.status !== ComposeSnapshotStatus.DIRTY
        ) {
          return state;
        }
        return {
          entriesBySessionId: {
            ...state.entriesBySessionId,
            [snapshot.sessionId]: toIndexEntry(snapshot),
          },
        };
      }
      if (
        change.userId !== state.userId ||
        !(change.sessionId in state.entriesBySessionId)
      ) {
        return state;
      }
      return {
        entriesBySessionId: withoutKey(
          state.entriesBySessionId,
          change.sessionId,
        ),
      };
    }),

  removeEntry: (sessionId) =>
    set((state) =>
      sessionId in state.entriesBySessionId
        ? {
            entriesBySessionId: withoutKey(state.entriesBySessionId, sessionId),
          }
        : state,
    ),

  registerLiveSession: (sessionId) =>
    set((state) => ({
      liveSessionIds: { ...state.liveSessionIds, [sessionId]: true },
    })),

  unregisterLiveSession: (sessionId) =>
    set((state) => ({
      liveSessionIds: withoutKey(state.liveSessionIds, sessionId),
      lastLiveAtBySessionId: {
        ...state.lastLiveAtBySessionId,
        [sessionId]: Date.now(),
      },
    })),

  registerOpenCompose: (sessionId) =>
    set((state) => ({
      openComposeSessionIds: {
        ...state.openComposeSessionIds,
        [sessionId]: true,
      },
    })),

  unregisterOpenCompose: (sessionId) =>
    set((state) =>
      sessionId in state.openComposeSessionIds
        ? {
            openComposeSessionIds: withoutKey(
              state.openComposeSessionIds,
              sessionId,
            ),
          }
        : state,
    ),

  claimSourceSession: (sessionId) =>
    set((state) => ({
      claimedSourceSessionIds: {
        ...state.claimedSourceSessionIds,
        [sessionId]: true,
      },
    })),

  releaseSourceSession: (sessionId) =>
    set((state) =>
      sessionId in state.claimedSourceSessionIds
        ? {
            claimedSourceSessionIds: withoutKey(
              state.claimedSourceSessionIds,
              sessionId,
            ),
          }
        : state,
    ),

  markBackgroundSaving: (sessionId) =>
    set((state) => ({
      backgroundSavingSessionIds: {
        ...state.backgroundSavingSessionIds,
        [sessionId]: true,
      },
    })),

  unmarkBackgroundSaving: (sessionId) =>
    set((state) =>
      sessionId in state.backgroundSavingSessionIds
        ? {
            backgroundSavingSessionIds: withoutKey(
              state.backgroundSavingSessionIds,
              sessionId,
            ),
          }
        : state,
    ),

  markSyncing: (sessionId) =>
    set((state) => ({
      syncingSessionIds: { ...state.syncingSessionIds, [sessionId]: true },
    })),

  unmarkSyncing: (sessionId) =>
    set((state) => ({
      syncingSessionIds: withoutKey(state.syncingSessionIds, sessionId),
    })),

  recordSyncedDraft: (sessionId, draftId) =>
    set((state) => ({
      syncedDraftIdBySessionId: {
        ...state.syncedDraftIdBySessionId,
        [sessionId]: draftId,
      },
    })),

  markDraftVerified: (draftId) =>
    set((state) =>
      draftId in state.verifiedDraftIds
        ? state
        : { verifiedDraftIds: { ...state.verifiedDraftIds, [draftId]: true } },
    ),

  resetState: () => set(INITIAL_COMPOSE_SNAPSHOT_INDEX_STATE),
});
